import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { WaitlistModal } from "@/components/WaitlistModal";
import { toast } from "sonner";

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const { 
    items, 
    isLoading, 
    isDemoMode,
    updateQuantity, 
    removeItem, 
    createCheckout,
    trackCheckoutAttempt
  } = useCartStore();
  
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);

  const handleCheckout = async () => {
    if (isDemoMode) {
      // Track the attempt and show waitlist modal
      await trackCheckoutAttempt();
      setShowWaitlist(true);
    } else {
      // Normal checkout flow
      try {
        await createCheckout();
        const checkoutUrl = useCartStore.getState().checkoutUrl;
        if (checkoutUrl) {
          window.open(checkoutUrl, '_blank');
          setIsOpen(false);
        }
      } catch (error) {
        console.error('Checkout failed:', error);
        toast.error("Failed to create checkout", {
          description: "Please try again later"
        });
      }
    }
  };

  return (
    <>
      <WaitlistModal open={showWaitlist} onOpenChange={setShowWaitlist} />
      
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button 
            variant="outline" 
            size="icon" 
            className="relative"
            aria-label={`Shopping cart${totalItems > 0 ? `, ${totalItems} item${totalItems !== 1 ? 's' : ''}` : ', empty'}`}
          >
            <ShoppingCart className="h-5 w-5" />
            {totalItems > 0 && (
              <Badge 
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-accent"
                aria-hidden="true"
              >
                {totalItems}
              </Badge>
            )}
          </Button>
        </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle>Shopping Cart</SheetTitle>
          <SheetDescription>
            {totalItems === 0 ? "Your cart is empty" : `${totalItems} item${totalItems !== 1 ? 's' : ''} in your cart`}
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Your cart is empty</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => {
                    const linkTo = item.customDesignId 
                      ? `/design/${item.customDesignId}` 
                      : `/product/${item.product.node.handle}`;
                    
                    return (
                    <div key={item.variantId} className="flex gap-4 p-2 border rounded-lg">
                      <Link 
                        to={linkTo}
                        onClick={() => setIsOpen(false)}
                        className="w-16 h-16 bg-secondary/20 rounded-md overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                      >
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img
                            src={item.product.node.images.edges[0].node.url}
                            alt={item.product.node.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </Link>
                      
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={linkTo}
                          onClick={() => setIsOpen(false)}
                          className="hover:text-primary transition-colors"
                        >
                          <h4 className="font-medium truncate">{item.product.node.title}</h4>
                        </Link>
                        <p className="text-sm text-muted-foreground">
                          {item.selectedOptions.map(option => option.value).join(' • ')}
                        </p>
                        <p className="font-semibold">
                          {item.price.currencyCode} {parseFloat(item.price.amount).toFixed(2)}
                        </p>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 sm:h-6 sm:w-6"
                          onClick={() => removeItem(item.variantId)}
                          aria-label={`Remove ${item.product.node.title} from cart`}
                        >
                          <Trash2 className="h-4 w-4 sm:h-3 sm:w-3" />
                        </Button>
                        
                        <div className="flex items-center gap-2 sm:gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 sm:h-6 sm:w-6"
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            aria-label={`Decrease quantity of ${item.product.node.title}`}
                          >
                            <Minus className="h-4 w-4 sm:h-3 sm:w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm" aria-label={`Quantity: ${item.quantity}`}>{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 sm:h-6 sm:w-6"
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            aria-label={`Increase quantity of ${item.product.node.title}`}
                          >
                            <Plus className="h-4 w-4 sm:h-3 sm:w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="flex-shrink-0 space-y-4 pt-4 border-t bg-background">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-xl font-bold">
                    {items[0]?.price.currencyCode || '$'} {totalPrice.toFixed(2)}
                  </span>
                </div>
                
                <Button 
                  onClick={handleCheckout}
                  className="w-full" 
                  size="lg"
                  disabled={items.length === 0 || isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : isDemoMode ? (
                    "Continue to Checkout"
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Checkout with Shopify
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
};
